import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useResize } from 'src/hooks/useResize';
import { useSelector } from 'react-redux';
import { Button, Col, Grid, Modal, Row, SelectPicker, Text } from 'rsuite';
import dayjs from 'dayjs';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import {
  C_LAST_12_MONTHS,
  C_PERMIT_STATUS_SUBMITTED,
} from '../../../common/consts';
import { PAYMENT_METHOD_LABEL_ENUM } from '../../../common/enums';
import { getAddressWithUnitNumber, titleize } from '../../../common/utils';
import Notice from '../../user-feedback/Notice';
import IconIndicator from '../../user-feedback/IconIndicator';
import ResponsiveModal from '../../ResponsiveModal';
import TaxPayableBreakdownTable from '../../tax/BreakdownTable/TaxPayableBreakdownTable';
import {
  selectReportingHistory,
  selectReportingHistoryErrorMsg,
  selectReportingHistoryInProgress,
  selectReportingHistorySuccess,
} from '../../../store/slices/licenseView/licenseViewSelectors';
import { TaxableActivity } from '../../../types/tax';
import { License } from '../../../types/licenses';
import ReportedLicensePeriod from './ReportedLicensePeriod';
import UnreportedLicensePeriod from './UnreportedLicensePeriod';

interface ConvertedLicense {
  daysAvailable?: number;
  daysOccupied?: number;
  endDate: string;
  hideTaxFlow?: boolean;
  isPending?: boolean;
  label: string;
  paidBy?: string;
  paymentAmount?: number;
  paymentSuccessful?: boolean;
  processingFee?: number;
  providers: TaxableActivity[];
  reportedBy?: string;
  reportedDate?: string;
  startDate: string;
  totalTaxableReceipts?: number;
  uniqueKey: string;
}

interface ReportingHistoryProps {
  applicationNumber: string | undefined;
  license?: License | null;
}

const ReportingHistory = ({
  applicationNumber,
  license,
}: ReportingHistoryProps) => {
  const reportingHistory = useSelector(selectReportingHistory);
  const reportingHistoryLoading = useSelector(selectReportingHistoryInProgress);
  const reportingHistorySuccess = useSelector(selectReportingHistorySuccess);
  const reportingHistoryErrorMesage = useSelector(
    selectReportingHistoryErrorMsg
  );

  const [selectedYearFilter, setSelectedYearFilter] = useState<string | number>(
    C_LAST_12_MONTHS
  );
  const [taxSummaryModalOpen, setTaxSummaryModalOpen] = useState(false);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [selectedLicense, setSelectedLicense] =
    useState<ConvertedLicense | null>(null);
  const { isMobile } = useResize();

  const showReportingHistory = useMemo(() => {
    return reportingHistory?.status !== C_PERMIT_STATUS_SUBMITTED;
  }, [reportingHistory?.status]);

  const convertedData = useMemo<ConvertedLicense[]>(() => {
    const convertedLicenses: ConvertedLicense[] = [];

    if (reportingHistory) {
      const sortedPeriods = [...reportingHistory.paymentPeriods].sort(
        (a, b) => {
          return dayjs(b.startDate).diff(dayjs(a.startDate));
        }
      );

      sortedPeriods?.forEach((period) => {
        if (period.payments.length) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          period.payments.forEach((payment: any, index: number) => {
            const convertedPayment: ConvertedLicense = {
              daysAvailable: payment.daysAvailable,
              daysOccupied: payment.daysOccupied,
              endDate: period.endDate,
              isPending: payment.isPending,
              label: period.label,
              paidBy:
                PAYMENT_METHOD_LABEL_ENUM[
                  payment.paymentProvider as keyof typeof PAYMENT_METHOD_LABEL_ENUM
                ] || payment.paymentProvider,
              paymentAmount: payment.fractionalPaymentTotal,
              paymentSuccessful: payment.paymentSuccessful,
              processingFee: payment.fractionalPaymentProcessingFee,
              providers: [],
              reportedBy: payment.reporter,
              reportedDate: dayjs(payment.paymentDate).format('YYYY-MM-DD'),
              startDate: period.startDate,
              totalTaxableReceipts: payment.taxableReceipts,
              uniqueKey: `reported_${period.startDate}_${period.endDate}_${payment.paymentId}_${index}`,
            };

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            payment.taxableActivities.forEach((activity: any) => {
              const provider: TaxableActivity = {
                deductions: activity.allowableDeductions,
                key: activity.provider || 'All',
                sales: activity.totalRevenue,
                taxableReceipts: activity.taxableReceipts,
                taxTypes: activity.taxTypes,
              };

              convertedPayment.providers.push(provider);
            });
            convertedLicenses.push(convertedPayment);
          });
        } else {
          const convertedPayment: ConvertedLicense = {
            endDate: period.endDate,
            hideTaxFlow: period.hideTaxFlow,
            label: period.label,
            providers: [],
            startDate: period.startDate,
            uniqueKey: `unreported_${period.startDate}_${period.endDate}`,
          };
          convertedLicenses.push(convertedPayment);
        }
      });
    }

    return convertedLicenses;
  }, [reportingHistory]);

  const filteredData = useMemo<ConvertedLicense[]>(() => {
    if (selectedYearFilter === C_LAST_12_MONTHS) {
      return convertedData?.filter(
        (item) =>
          Math.abs(dayjs(item.startDate).diff(dayjs(new Date()), 'month')) < 12
      );
    } else {
      return convertedData?.filter(
        (item) => dayjs(item.startDate).year() === selectedYearFilter
      );
    }
  }, [convertedData, selectedYearFilter]);

  const totPayable = useMemo(() => {
    return reportingHistory?.totPayable || false;
  }, [reportingHistory]);

  const filterOptions = useMemo(() => {
    const years = orderBy(
      uniq(convertedData.map(({ startDate }) => dayjs(startDate).year())),
      (e) => e,
      ['desc']
    );

    const yearOptions = years.map((year) => ({ name: year, value: year }));

    return [
      { name: 'Last 12 months', value: C_LAST_12_MONTHS },
      ...yearOptions,
    ];
  }, [convertedData]);

  const address = useMemo(() => {
    return getAddressWithUnitNumber(
      reportingHistory?.propertyInfo?.propertyAddress,
      reportingHistory?.propertyInfo?.unitNumber
    );
  }, [reportingHistory]);

  const licenseNumber = useMemo(() => {
    return reportingHistory?.certificateInfo?.licenseNumber;
  }, [reportingHistory]);

  const licenseSelected = useCallback(
    (eventKey: string) => {
      if (activeKeys.includes(eventKey)) {
        const newActiveKeys = activeKeys.filter((item) => item !== eventKey);
        setActiveKeys(newActiveKeys);
      } else {
        const newActiveKey = [...activeKeys, eventKey];
        const selected = filteredData?.find(
          (license) => license.uniqueKey === eventKey
        );
        setSelectedLicense(selected ?? null);
        setActiveKeys(newActiveKey);
      }
    },
    [filteredData, activeKeys]
  );

  useEffect(() => {
    setTaxSummaryModalOpen(isMobile && !!selectedLicense);
  }, [isMobile, selectedLicense]);

  const clearSelectedLicenseModalClose = useCallback(() => {
    setTaxSummaryModalOpen(false);
    setSelectedLicense(null);
    const newActiveKeys = activeKeys.filter(
      (item) => item !== selectedLicense?.uniqueKey
    );
    setActiveKeys(newActiveKeys);
  }, [activeKeys, selectedLicense]);

  const panelExpanded = useCallback(
    (key: string) => activeKeys?.includes(key),
    [activeKeys]
  );

  const handleYearFilterSelect = useCallback((value: string | number) => {
    setSelectedYearFilter(value);
  }, []);

  const handleModalClose = useCallback(() => {
    clearSelectedLicenseModalClose();
  }, [clearSelectedLicenseModalClose]);

  return (
    <>
      <Grid fluid>
        <Row className="mb-24">
          <Col
            sm={12}
            xs={24}
          >
            <Text
              as="h3"
              className="text-bold header-text-h4"
            >
              Reporting History
            </Text>
          </Col>
          {!reportingHistoryLoading && showReportingHistory && (
            <Col
              className="pb-16-sm"
              md={6}
              mdPush={6}
              sm={12}
              xs={24}
            >
              <SelectPicker
                cleanable={false}
                className="sm-100-percent filter-picker"
                data={filterOptions}
                labelKey="name"
                menuStyle={{ width: '199px' }}
                onSelect={handleYearFilterSelect}
                searchable={false}
                style={{ width: '200px', float: 'right' }}
                value={selectedYearFilter}
                valueKey="value"
              />
            </Col>
          )}
        </Row>
        {reportingHistoryLoading && (
          <Row>
            <Col xs={24}>
              <IconIndicator />
            </Col>
          </Row>
        )}
        {!reportingHistoryLoading &&
          reportingHistorySuccess &&
          (filteredData.length ? (
            <Row
              className="content-section"
              style={{ minHeight: '258px' }}
            >
              <Col xs={24}>
                {showReportingHistory &&
                  filteredData.map((licensePeriod) => (
                    <div
                      className="reporting-history-item"
                      key={licensePeriod.uniqueKey}
                    >
                      {licensePeriod.providers.length > 0 ? (
                        <ReportedLicensePeriod
                          expanded={panelExpanded(licensePeriod.uniqueKey)}
                          isMobile={isMobile}
                          licensePeriod={licensePeriod}
                          onLicenseSelected={licenseSelected}
                        />
                      ) : (
                        <UnreportedLicensePeriod
                          address={address}
                          applicationNumber={applicationNumber}
                          hideTaxFlow={licensePeriod.hideTaxFlow}
                          license={license}
                          licenseNumber={licenseNumber}
                          periodEndDate={licensePeriod.endDate}
                          periodLabel={licensePeriod.label}
                          periodStartDate={licensePeriod.startDate}
                          totPayable={totPayable}
                        />
                      )}
                    </div>
                  ))}
              </Col>
            </Row>
          ) : (
            <Row>
              <Col xs={24}>
                <Notice
                  message="No reported history for the selected period"
                  type="info"
                />
              </Col>
            </Row>
          ))}
        {!reportingHistorySuccess &&
          !reportingHistoryLoading &&
          reportingHistoryErrorMesage && (
            <Notice message={reportingHistoryErrorMesage} />
          )}
      </Grid>

      <ResponsiveModal
        ariaLabel="reporting-history"
        isOpen={taxSummaryModalOpen}
        onClose={handleModalClose}
      >
        {selectedLicense && (
          <>
            <Modal.Body
              aria-label="modal-reporting-history"
              className="ph-12 pv-8"
              style={{ maxHeight: 'unset', marginTop: '10px' }}
            >
              <Grid fluid>
                <Row>
                  <Col xs={24}>
                    <Text className="text-bold mb-16">
                      {selectedLicense?.label}
                    </Text>
                  </Col>
                </Row>
                {reportingHistory?.propertyInfo?.propertyAddress && (
                  <Row>
                    <Col xs={24}>
                      <Text className="text-bold mb-16">
                        {titleize(
                          reportingHistory?.propertyInfo?.propertyAddress
                        )}
                      </Text>
                    </Col>
                  </Row>
                )}
              </Grid>
              <TaxPayableBreakdownTable
                license={selectedLicense?.providers}
                period={selectedLicense}
                periodLabel={selectedLicense?.label}
                processingFee={selectedLicense.processingFee}
                totalPayment={selectedLicense.paymentAmount}
              />
            </Modal.Body>
            <Modal.Footer style={{ padding: '10px' }}>
              <Button
                className="btn-primary"
                onClick={handleModalClose}
                style={{ width: '100%' }}
              >
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </ResponsiveModal>
    </>
  );
};

export default ReportingHistory;
